/**
 * time.js — the single source of truth for time formatting in Viyora.
 *
 * Design rule (Feb 2026 standardization): every visible time in Viyora
 * uses **24-hour HH:mm**.  No AM/PM anywhere.  If you find yourself
 * calling `toLocaleTimeString(..., { hour12: true })` — stop and use one
 * of the helpers here instead.
 *
 * Everything accepts either a Date, an ISO string, or a raw "HH:mm"
 * string so callers don't need to normalise first.
 */

/** Zero-pad any non-negative integer to two digits ("9" → "09"). */
const p2 = (n) => String(Math.max(0, Math.trunc(Number(n) || 0))).padStart(2, "0");

/** Coerce anything time-ish into a Date, or return null on failure. */
function toDate(v) {
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === "string") {
    // Bare "HH:mm" — anchor to today so downstream .getHours() works.
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(v)) {
      const [h, m, s] = v.split(":").map(Number);
      const d = new Date();
      d.setHours(h || 0, m || 0, s || 0, 0);
      return d;
    }
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  return null;
}

/** Format a time value as `HH:mm` (24-hour, zero-padded). Returns "" on invalid. */
export function fmtTime24(v) {
  const d = toDate(v);
  return d ? `${p2(d.getHours())}:${p2(d.getMinutes())}` : "";
}

/** Same as fmtTime24 but includes seconds — used by audit logs. */
export function fmtTime24s(v) {
  const d = toDate(v);
  return d ? `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}` : "";
}

/** Format a date-time as `dd MMM yyyy · HH:mm` — the canonical timestamp
 * shown next to timeline entries, movement rows, invoices, etc. */
export function fmtDateTime24(v) {
  const d = toDate(v);
  if (!d) return "";
  const day  = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return `${day} · ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/** Format just the date portion — kept here so callers have one import. */
export function fmtDate(v) {
  const d = toDate(v);
  return d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
}

/** Convert `HH:mm` string → minutes since midnight (used by scheduling
 * engine comparisons — the entire booking pipeline should be pure
 * integer math, not string parsing). */
export function timeToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return (Number(h) || 0) * 60 + (Number(m) || 0);
}

/** Reverse of the above. */
export function minutesToTime(mins) {
  const m = Math.max(0, Math.trunc(Number(mins) || 0));
  return `${p2(Math.floor(m / 60))}:${p2(m % 60)}`;
}
